import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axios.get(`${API}/case-studies/${slug}`);
        setStudy(response.data);
      } catch (err) {
        setError("Case study not found");
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudy();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !study) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Case Study Not Found</h1>
          <Link to="/case-studies">
            <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Case Studies
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="case-study-detail-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <Link 
            to="/case-studies" 
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Case Studies
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-xs font-bold uppercase tracking-wider text-blue-400">
                  {study.industry}
                </span>
                <span className="text-slate-600">|</span>
                <span className="text-slate-400">
                  {study.client}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white font-['Outfit'] mb-6">
                {study.title}
              </h1>
              <div className="flex flex-wrap gap-2">
                {study.services_used.map((service) => (
                  <span 
                    key={service}
                    className="px-3 py-1 bg-slate-800 border border-slate-700 text-slate-300 text-xs font-medium"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
            <div className="aspect-[16/10] overflow-hidden border border-slate-700">
              <img 
                src={study.image_url}
                alt={study.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Challenge */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                  The Challenge
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                  {study.challenge}
                </p>
              </div>

              {/* Solution */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                  Our Solution
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                  {study.solution}
                </p>
              </div>

              {/* Testimonial */}
              {study.testimonial && (
                <div className="bg-slate-50 p-8 border-l-4 border-blue-700">
                  <Quote className="h-8 w-8 text-blue-200 mb-4" />
                  <blockquote className="text-lg text-slate-700 leading-relaxed font-['Plus_Jakarta_Sans'] mb-4">
                    "{study.testimonial}"
                  </blockquote>
                  {study.testimonial_author && (
                    <cite className="text-sm text-slate-500 not-italic">
                      — {study.testimonial_author}
                    </cite>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - Results */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-slate-900 p-8">
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-6">
                  Key Results
                </h2>
                <ul className="space-y-4">
                  {study.results.map((result) => (
                    <li key={result} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white font-['Plus_Jakarta_Sans']">
                        {result}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Ready to Achieve Similar Results?
          </h2>
          <p className="text-blue-100 mb-8 font-['Plus_Jakarta_Sans']">
            Let's discuss how we can help transform your business.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-none px-8"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
